import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import {
  Container,
  Box,
  Button,
  Typography,
  styled,
  IconButton,
  Grid,
  Stack,
  Menu,
  MenuItem,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogContent,
  InputBase,
  Fab,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  DialogTitle,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Link,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  KeyboardArrowDown,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Chat as ChatIcon,
  Send as SendIcon,
  Article as ArticleIcon,
  Book as BookIcon,
  Collections as CollectionsIcon,
  Build as BuildIcon,
  DevicesOther,
  Computer,
  HomeRepair,
  Smartphone,
  CloudQueue,
  Games,
  Handyman,
  Settings,
  Construction,
  SportsEsports,
  Hardware as HardwareIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  PriceCheck as PriceCheckIcon,
  BusinessCenter as BusinessIcon,
  Support as SupportIcon,
  Engineering as EngineeringIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  PrivacyTip as PrivacyTipIcon,
  Gavel as GavelIcon,
  ArrowForward as ArrowForwardIcon,
  ShoppingCart as ShoppingCartIcon,
  VideoCall as VideoCallIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import LogoImage from '../assets/logo0.png';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SearchIconImage from '../assets/search-interface-symbol.png';
import Search2Icon from '../assets/search2.png';
import FingerprintIcon from '../assets/fingerprint.png';
import repairImage from '../assets/repair.png';
import smartphonesImage from '../assets/smartphones.png';
import computersImage from '../assets/computers.png';
import suppliesImage from '../assets/supplies.png';
import gamingImage from '../assets/gaming.png';
import cloudImage from '../assets/cloud.png';
import iotImage from '../assets/iot.png';
import Chatbot from '../components/ChatBot';
import { keyframes } from '@mui/material/styles';
import SwitchIcon from '../assets/switch.png';
import CookieIcon from '../assets/cookie2.png';

// First, let's define a constant for header height
const HEADER_HEIGHT = '60px'; // Adjusted to match the screenshot

// First, add these animation keyframes at the top of your file (if not already there)
// Make sure to import keyframes from '@mui/material/styles' at the top of your file
const float = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(3deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const drift = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(20px); }
  100% { transform: translateX(0); }
`;

// Styled Components
const StyledHeader = styled('header')(({ theme }) => ({
  backgroundColor: '#000',
  borderBottom: '1px solid #007C3F', // Green border to match the brand
  width: '100%',
  position: 'fixed',
  top: 0,
  zIndex: 1000,
  height: HEADER_HEIGHT, // Fixed height to match screenshot
}));

const SearchBar = styled('div')(({ theme }) => ({
  backgroundColor: '#007C3F',
  padding: '15px 0',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: -100,
    width: '200%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #000, #fff, #000, #fff, #000, transparent)',
    animation: 'moveGradient 3s linear infinite'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: -100,
    width: '200%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #fff, #000, #fff, #000, #fff, transparent)',
    animation: 'moveGradientReverse 3s linear infinite'
  },
  '@keyframes moveGradient': {
    '0%': { transform: 'translateX(0%)' },
    '100%': { transform: 'translateX(50%)' }
  },
  '@keyframes moveGradientReverse': {
    '0%': { transform: 'translateX(0%)' },
    '100%': { transform: 'translateX(-50%)' }
  }
}));

const NavLink = styled(Typography)({
  color: '#fff',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  '&:hover': {
    color: '#FFE302'
  }
});

const SearchInputWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'center'
}));

const SearchInput = styled('input')(({ theme }) => ({
  width: '100%',
  padding: '12px 16px 12px 40px',
  border: 'none',
  outline: 'none',
  fontSize: '15px',
  fontFamily: 'Arial, sans-serif',
  borderRadius: '0',
  boxShadow: 'none',
  '&::placeholder': {
    color: '#666',
  },
  '&:focus': {
    boxShadow: 'none'
  }
}));

const SearchIconImg = styled('img')({
  position: 'absolute',
  left: '10px',
  width: '20px',
  height: '20px',
  pointerEvents: 'none'
});

const ScanButton = styled(Button)({
  backgroundColor: '#333',
  color: '#fff',
  textTransform: 'none',
  padding: '8px 16px',
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: '#444',
  },
});

const DomainButton = styled(Button)({
  backgroundColor: '#333',
  color: '#fff',
  textTransform: 'none',
  padding: '8px 16px',
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: '#444',
  },
});

const DomainOption = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
  color: '#333',
  width: '40px',
  height: '40px',
  borderRadius: 0,
  fontFamily: 'Arial, sans-serif',
  fontWeight: 500,
});

const LogoWrapper = styled(Box)({
  cursor: 'pointer',
  height: '34px',
  marginRight: '24px',
  display: 'flex',
  alignItems: 'center',
  '& img': {
    height: '100%',
    width: 'auto',
    filter: 'brightness(0) invert(1)'
  }
});

const CookieBanner = ({ open, onClose, onAccept }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [localPreferences, setLocalPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  const handlePreferenceChange = (name) => (event) => {
    setLocalPreferences({
      ...localPreferences,
      [name]: event.target.checked
    });
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    onAccept(allAccepted);
    onClose();
  };

  const handleSavePreferences = () => {
    onAccept(localPreferences);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 0,
          m: 2,
          bgcolor: '#000',
          color: '#fff'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: 1, 
        borderColor: 'rgba(255,255,255,0.1)',
        color: '#fff'
      }}>
        <Typography variant="h5" component="div">
          Cookie Settings
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              mr: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <img 
                src={CookieIcon} 
                alt="Cookie" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%',
                  objectFit: 'contain'
                }} 
              />
            </Box>
            <Typography paragraph color="white">
              We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
              By clicking "Accept All", you consent to our use of cookies.
            </Typography>
          </Box>

          <Accordion 
            expanded={showDetails}
            onChange={() => setShowDetails(!showDetails)}
            sx={{ 
              mt: 2,
              bgcolor: '#000',
              color: '#fff',
              '&.MuiAccordion-root': {
                borderRadius: 0
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">
                Customize Cookie Preferences
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={localPreferences.necessary}
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="medium">
                        Necessary Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        These cookies are essential for the website to function properly and cannot be disabled.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={localPreferences.analytics}
                      onChange={handlePreferenceChange('analytics')}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="medium">
                        Analytics Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={localPreferences.marketing}
                      onChange={handlePreferenceChange('marketing')}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="medium">
                        Marketing Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Used to track visitors across websites to display relevant advertisements.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={localPreferences.functional}
                      onChange={handlePreferenceChange('functional')}
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="medium">
                        Functional Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable enhanced functionality and personalization.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              For more information about our cookie practices, please view our{' '}
              <Link href="/privacy-policy" color="primary" sx={{ color: '#FFE302' }}>
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: 1, 
        borderColor: 'rgba(255,255,255,0.1)',
      }}>
        <Button 
          onClick={() => setShowDetails(!showDetails)}
          sx={{ 
            color: '#fff',
            borderRadius: 0
          }}
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button 
          onClick={handleSavePreferences}
          sx={{
            color: '#000',
            bgcolor: '#fff',
            borderRadius: 0,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)'
            },
            mr: 1
          }}
        >
          Save Preferences
        </Button>
        <Button 
          onClick={handleAcceptAll}
          sx={{
            color: '#000',
            bgcolor: '#FFE302',
            borderRadius: 0,
            '&:hover': {
              bgcolor: '#FFE302CC'
            }
          }}
        >
          Accept All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Footer = styled('footer')({
  backgroundColor: '#000',
  color: '#fff',
  padding: '48px 0 24px',
  marginTop: 'auto',
});

const FooterLink = styled(Typography)({
  color: '#fff',
  cursor: 'pointer',
  '&:hover': {
    color: '#ccc'
  }
});

const SocialIconButton = styled(IconButton)({
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  }
});

const ExploreSection = styled(Box)({
  padding: '40px 0',
  backgroundColor: '#fff',
});

const CategoryButton = styled(Button)({
  textTransform: 'none',
  padding: '8px 16px',
  borderRadius: 0,
  border: '1px solid #000',
  '&.active': {
    backgroundColor: '#FFE600',
    borderColor: '#FFE600',
  },
  '&:hover': {
    backgroundColor: '#FFE600',
    borderColor: '#FFE600',
  }
});

const BookCard = styled(Box)({
  display: 'flex',
  gap: '24px',
  backgroundColor: '#f5f5f5',
  padding: '24px',
  borderRadius: '8px',
  marginTop: '24px',
});

const SocialIcons = styled(Stack)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const HeaderMenu = styled(Menu)({
  marginTop: '8px',
  '& .MuiPaper-root': {
    borderRadius: 0,
    minWidth: '200px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  }
});

const StyledMenuItem = styled(MenuItem)({
  padding: '8px 16px',
  fontWeight: 600,
  fontSize: '14px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  }
});

const AuthButton = styled(Button)({
  borderRadius: 0,
  textTransform: 'none',
  padding: '8px 16px',
});

const NavContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const SearchIconWrapper = styled(Box)({
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  '&:hover': {
    opacity: 0.8
  }
});

const IconWrapper = styled(IconButton)({
  padding: '8px',
  color: '#000',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.04)'
  }
});

const SearchDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: '600px',
    borderRadius: 0,
  }
}));

const ChatButton = styled(Fab)({
  position: 'fixed',
  bottom: 24,
  right: 24,
  backgroundColor: '#000',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#333',
  }
});

const ChatBox = styled(Box)({
  position: 'fixed',
  bottom: 90,
  right: 24,
  width: '300px',
  height: '400px',
  backgroundColor: '#fff',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const serviceItems = [
  {
    title: 'AI-Powered Diagnostics',
    description: 'Instantly analyze device issues with AI-driven fault detection, guiding users toward the best repair solutions.',
    image: require('../assets/dashboard1.png'),
    alt: 'AI Diagnostics Dashboard',
    link: '/ai-diagnostics'
  },
  {
    title: 'Expert Video Consultations',
    description: 'Connect with certified technicians for real-time guidance on troubleshooting and fixing complex hardware problems.',
    image: require('../assets/dashboard2.png'),
    alt: 'Video Consultation Interface',
    link: '/video-consultations'
  },
  {
    title: 'Step-by-Step Repair Guides',
    description: 'Follow easy-to-understand repair tutorials designed to help users fix their devices efficiently and safely.',
    image: require('../assets/dashboard3.png'),
    alt: 'Repair Guide Dashboard',
    link: '/repair-guides'
  },
  {
    title: 'Genuine Spare Parts Marketplace',
    description: 'Access a secure marketplace with verified suppliers, ensuring you get high-quality replacement parts at competitive prices.',
    image: require('../assets/dashboard4.png'),
    alt: 'Parts Marketplace Interface',
    link: '/parts-marketplace'
  }
];

const categories = [
  {
    id: 'smartphones',
    label: 'Smartphones',
    description: 'Identify and resolve mobile issues such as screen damage, battery failure, and software glitches using AI-driven diagnostics and repair recommendations.',
    image: smartphonesImage,
    stats: {
      repairs: '15K+',
      success: '98%'
    }
  },
  {
    id: 'computers',
    label: 'Computers & Laptops',
    description: 'Optimize performance, detect hardware failures, and troubleshoot system errors with AI-based fault detection and cloud repair history tracking.',
    image: computersImage,
    stats: {
      repairs: '12K+',
      success: '95%'
    }
  },
  {
    id: 'appliances',
    label: 'Home Appliances',
    description: 'Fix household devices like dishwashers, refrigerators, and washing machines with AI-assisted troubleshooting, ensuring longer-lasting performance.',
    image: suppliesImage,
    stats: {
      repairs: '8K+',
      success: '94%'
    }
  },
  {
    id: 'gaming',
    label: 'Gaming Consoles',
    description: 'Resolve overheating, connectivity, and hardware malfunctions in gaming systems with AI-powered insights and maintenance solutions.',
    image: gamingImage,
    stats: {
      repairs: '6K+',
      success: '96%'
    }
  },
  {
    id: 'cloud',
    label: 'Cloud Services',
    description: 'Store and access your repair history, AI-driven diagnostics, and service reports securely in the cloud, ensuring efficient tracking of device issues.',
    image: cloudImage,
    stats: {
      repairs: '20K+',
      success: '99%'
    }
  },
  {
    id: 'iot',
    label: 'IoT Devices',
    description: 'Detect and fix smart home device issues, from connectivity problems to firmware updates, ensuring seamless operation of your IoT ecosystem.',
    image: iotImage,
    stats: {
      repairs: '10K+',
      success: '97%'
    }
  }
];

const RepairSection = () => {
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [showDescription, setShowDescription] = useState(false);
  const navigate = useNavigate();

  return (
    <Box component="section" sx={{ 
      bgcolor: '#FFFFFF', // Keeping the original white/gray background
      pt: 2,
      pb: 8,
      width: '100%',
      position: 'relative', // Ensure proper stacking
      overflow: 'hidden' // Important to contain the shapes
    }}>
      {/* Stylish geometric background elements with animations */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        pointerEvents: 'none'
      }}>
        {/* Large circle - top left */}
        <Box sx={{ 
                    position: 'absolute',
          top: '-100px',
          left: '-100px',
                    width: '300px',
          height: '300px',
        borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.25)', // Adjusted transparency
          animation: `${pulse} 8s ease-in-out infinite`,
        }} />
        
        {/* Medium circle - bottom right */}
        <Box sx={{
          position: 'absolute',
          bottom: '-80px',
          right: '-80px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.22)', // Adjusted transparency
          animation: `${float} 12s ease-in-out infinite`,
        }} />
        
        {/* Small circle - center right */}
        <Box sx={{
          position: 'absolute',
          top: '40%',
          right: '15%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.28)', // Adjusted transparency
          animation: `${drift} 10s ease-in-out infinite`,
        }} />
        
        {/* Small circle - bottom left */}
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.25)', // Adjusted transparency
          animation: `${pulse} 6s ease-in-out infinite`,
          animationDelay: '2s',
        }} />
        
        {/* Triangle - top right */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          right: '20%',
          width: 0,
          height: 0,
          borderLeft: '50px solid transparent',
          borderRight: '50px solid transparent',
          borderBottom: '86px solid rgba(0, 0, 0, 0.2)', // Adjusted transparency
          animation: `${float} 9s ease-in-out infinite`,
          animationDelay: '1s',
        }} />
        
        {/* Triangle - center left */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '20%',
          width: 0,
          height: 0,
          borderLeft: '30px solid transparent',
          borderRight: '30px solid transparent',
          borderBottom: '52px solid rgba(0, 0, 0, 0.24)', // Adjusted transparency
          transform: 'rotate(30deg)',
          animation: `${drift} 7s ease-in-out infinite`,
          animationDelay: '3s',
        }} />
        
        {/* Square - top center */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          left: '45%',
          width: '70px',
          height: '70px',
          background: 'rgba(0, 0, 0, 0.22)', // Adjusted transparency
          animation: `${spin} 20s linear infinite`,
        }} />
        
        {/* Rectangle - bottom center */}
        <Box sx={{
          position: 'absolute',
          bottom: '15%',
          left: '40%',
          width: '120px',
          height: '60px',
          background: 'rgba(0, 0, 0, 0.23)', // Adjusted transparency
          animation: `${float} 11s ease-in-out infinite`,
          animationDelay: '2s',
        }} />
        
        {/* Yellow accent circle - mid right */}
        <Box sx={{
          position: 'absolute',
          top: '30%',
          right: '30%',
          width: '60px', // Larger
          height: '60px', // Larger
          borderRadius: '50%',
          background: 'rgba(255,227,2,0.5)', // Adjusted transparency
          animation: `${pulse} 5s ease-in-out infinite`,
        }} />
        
        {/* Yellow accent triangle - bottom left */}
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '5%',
          width: 0,
          height: 0,
          borderLeft: '25px solid transparent', // Larger
          borderRight: '25px solid transparent', // Larger
          borderBottom: '45px solid rgba(255, 227, 2, 0.45)', // Adjusted transparency
          animation: `${float} 8s ease-in-out infinite`,
          animationDelay: '1.5s',
        }} />
        
        {/* Additional yellow circle - top center */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '35%',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(255, 227, 2, 0.4)', // Adjusted transparency
          animation: `${drift} 9s ease-in-out infinite`,
          animationDelay: '1s',
        }} />
        
        {/* Additional black square - bottom right */}
        <Box sx={{
          position: 'absolute',
          bottom: '25%',
          right: '25%',
          width: '50px',
          height: '50px',
          background: 'rgba(0, 0, 0, 0.26)', // Adjusted transparency
          animation: `${spin} 15s linear infinite`,
          animationDirection: 'reverse',
        }} />
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hot Off The Press */}
        <Box 
          sx={{ 
            width: '100%',
            margin: 0,
            padding: 0,
            '& > section': {
              marginTop: 0,
              marginBottom: 0
            }
          }}
        >
          <section>
            <Typography 
              variant="h6" 
              sx={{ 
                backgroundColor: '#f5f5f5',
                padding: '16px',
                margin: 0
              }}
            >
              HOT OFF THE PRESS
            </Typography>
            {/* ... rest of the content ... */}
          </section>
        </Box>

        {/* Main Title - keeping original styling */}
        <Typography 
          variant="h1" 
          sx={{ 
            fontSize: { xs: '32px', md: '48px' },
            fontWeight: 900,
            letterSpacing: '-0.02em',
            color: '#000000',
            mb: 3,
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            lineHeight: 1.1
          }}
        >
          EXPLORE SMART REPAIRS
        </Typography>

        {/* Category Pills with Hover Effect */}
        <Box sx={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          mb: 5
        }}>
          {categories.map((category) => (
            <Button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              onMouseEnter={() => setShowDescription(true)}
              onMouseLeave={() => setShowDescription(false)}
              sx={{
                bgcolor: activeCategory === category.id ? '#FFE302' : 'transparent',
                border: '1px solid #E0E0E0',
                color: '#000000',
                px: 2.5,
                py: 0.75,
                minHeight: '36px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                position: 'relative',
                '&:hover': {
                  bgcolor: activeCategory === category.id ? '#FFE302' : '#F5F5F5',
                  '& .description': {
                    opacity: 1,
                    visibility: 'visible'
                  }
                }
              }}
            >
              {category.label}
              {showDescription && (
                <Box
                  className="description"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '300px',
                    p: 2,
                    bgcolor: '#000',
                    color: '#fff',
                    borderRadius: '4px',
                    opacity: 0,
                    visibility: 'hidden',
                    transition: 'all 0.3s ease',
                    zIndex: 10,
                    mt: 1,
                    fontSize: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                >
                  {category.description}
                </Box>
              )}
            </Button>
          ))}
        </Box>

        {/* Content Grid */}
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h2" sx={{ 
                fontSize: { xs: '24px', md: '32px' },
                fontWeight: 700,
                color: '#000000',
                mb: 2
              }}>
                {categories.find(cat => cat.id === activeCategory)?.label.toUpperCase()}
              </Typography>
              
              <Typography sx={{ 
                color: '#333333',
                fontSize: '16px',
                lineHeight: 1.6,
                mb: 3
              }}>
                {categories.find(cat => cat.id === activeCategory)?.description}
              </Typography>

              <Box sx={{ 
                display: 'flex',
                gap: 3,
                mb: 4
              }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {categories.find(cat => cat.id === activeCategory)?.stats.repairs}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Repairs Completed
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {categories.find(cat => cat.id === activeCategory)?.stats.success}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success Rate
                  </Typography>
                </Box>
              </Box>

              <Button
                onClick={() => navigate('/solutions')}
                sx={{
                  border: '1px solid #000',
                  color: '#000',
                  bgcolor: 'transparent',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    bgcolor: '#000',
                    color: '#fff'
                  }
                }}
              >
                EXPLORE SOLUTIONS
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ 
              width: '100%',
              height: '400px',
              bgcolor: '#F5F5F5',
              overflow: 'hidden'
            }}>
              <img
                src={categories.find(cat => cat.id === activeCategory)?.image}
                alt={categories.find(cat => cat.id === activeCategory)?.label}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      id: 1,
      title: 'AI-Powered Diagnostics',
      description: 'Users upload images or describe their device issues, and our AI analyzes the problem using advanced fault detection algorithms.',
      icon: '🔍'
    },
    {
      id: 2,
      title: 'Smart Repair Guidance',
      description: 'Based on the diagnosis, the platform provides step-by-step troubleshooting solutions, including software fixes, hardware repair instructions, and maintenance tips.',
      icon: '⚡'
    },
    {
      id: 3,
      title: 'Cloud-Based Tracking',
      description: 'All diagnostics and repair histories are securely stored, allowing users to monitor past issues, access expert recommendations, and track device performance over time.',
      icon: '☁️'
    }
  ];

  return (
    <Box 
      component="section" 
      sx={{ 
        bgcolor: '#000000',
        py: 8,
        color: 'white'
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ mb: 6, maxWidth: '800px' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '24px', md: '32px' },
              fontWeight: 700,
              mb: 3,
              color: '#FFE302'
            }}
          >
            How It Works
          </Typography>
          
          <Typography 
            sx={{ 
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#FFFFFF',
              opacity: 0.9
            }}
          >
            Larvarris AI is an intelligent web platform designed to simplify and enhance device repair services through cutting-edge artificial intelligence. Our system operates in three seamless steps:
          </Typography>
        </Box>

        {/* Steps Grid */}
        <Grid container spacing={4}>
          {steps.map((step) => (
            <Grid item xs={12} md={4} key={step.id}>
              <Box
                sx={{
                  height: '100%',
                  p: 3,
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#FFE302',
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontSize: '48px',
                      lineHeight: 1,
                      mb: 2
                    }}
                  >
                    {step.icon}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      color: '#FFE302'
                    }}
                  >
                    {step.title}
                  </Typography>
                </Box>
                
                <Typography 
                  sx={{ 
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.8)'
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Text */}
        <Box sx={{ mt: 6, maxWidth: '800px' }}>
          <Typography 
            sx={{ 
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#FFFFFF',
              opacity: 0.9
            }}
          >
            Whether you're dealing with a smartphone, computer, home appliance, or IoT device, Larvarris AI ensures fast, accurate, and cost-effective solutions—minimizing downtime and extending the life of your technology.
          </Typography>
        </Box>

        {/* CTA Button */}
        <Box sx={{ mt: 4 }}>
          <Link to="/diagnostics" style={{ textDecoration: 'none' }}>
            <button className="get-started" style={{ backgroundColor: '#FFE302', border: 'none', padding: '10px 20px', cursor: 'pointer' }}>
              GET STARTED
            </button>
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

const NavMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const menuItems = {
    options: [
      {
        title: 'Diagnostic Tools',
        items: ['Smart Scanner', 'Performance Analysis', 'System Check', 'Health Reports']
      },
      {
        title: 'Maintenance Tools',
        items: ['Regular Checks', 'Performance Tuning', 'System Updates', 'Optimization']
      }
    ],
    hardware: [
      {
        title: 'Device Support',
        items: ['Component Testing', 'Hardware Analysis', 'Part Replacement', 'Upgrade Guide']
      },
      {
        title: 'System Solutions',
        items: ['System Diagnosis', 'Performance Boost', 'Hardware Checks', 'Repair Tracking']
      }
    ],
    solutions: [
      {
        title: 'Enterprise Tools',
        items: ['Business Suite', 'Fleet Management', 'Bulk Diagnostics', 'Team Access']
      },
      {
        title: 'Technical Support',
        items: ['Expert Support', 'Remote Assistance', 'Training Resources', 'Documentation']
      }
    ],
    pricing: [
      {
        title: 'Service Plans',
        items: ['Basic Plan', 'Professional', 'Enterprise', 'Custom Solutions']
      },
      {
        title: 'ROI Calculator',
        items: ['Cost Analysis', 'Savings Estimate', 'Comparison Tool', 'Custom Quote']
      }
    ]
  };

  const handleMouseEnter = (key, event) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(key);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  return (
    <Box sx={{ bgcolor: '#FFE600' }}>
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          height: '48px',
          fontFamily: '"Helvetica Neue", Arial, sans-serif'
        }}>
          {Object.entries(menuItems).map(([key, items]) => (
            <Box
              key={key}
              onMouseEnter={(e) => handleMouseEnter(key, e)}
              onMouseLeave={handleMouseLeave}
              sx={{ position: 'relative' }}
            >
              <Button
                endIcon={<KeyboardArrowDown />}
                sx={{
                  color: '#000000',
                  textTransform: 'none',
                  px: 2,
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.08)'
                  }
                }}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Button>

              <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    mt: 1,
                    width: '280px',
                    bgcolor: '#FFFFFF',
                    borderRadius: 0,
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                  }}
                >
                  <Box sx={{ py: 1 }}>
                    {items.map((section, index) => (
                      <Box key={index}>
                        <Typography
                          sx={{
                            px: 2,
                            py: 1,
                            fontSize: '16px',
                            fontWeight: 700,
                            color: '#000000',
                            borderBottom: '1px solid #E0E0E0'
                          }}
                        >
                          {section.title}
                        </Typography>
                        {section.items.map((item, idx) => (
                          <Button
                            key={idx}
                            fullWidth
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              px: 2,
                              py: 1,
                              color: '#333333',
                              fontSize: '14px',
                              fontWeight: 400,
                              '&:hover': {
                                bgcolor: '#FFE600',
                                color: '#000000'
                              }
                            }}
                          >
                            {item}
                          </Button>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Popper>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

// Update the CircleNavLinks styling
const CircleNavLinks = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: '80px',
  display: 'flex',
  gap: '15px',
  padding: '8px 15px',
  backgroundColor: '#000',
  borderRadius: '0 0 15px 15px',
  transition: 'all 0.3s ease',
  transform: 'translateY(0)',
  zIndex: 1200,
  '@media (max-width: 768px)': {
    display: 'none'
  },
  '&.hidden': {
    transform: 'translateY(-100%)',
    opacity: 0
  }
}));

const LinkWithIcon = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  color: '#ffd700',
  textDecoration: 'none',
  fontSize: '14px',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#fff'
  }
});

const ShowcaseSection = () => {
  const services = [
    {
      title: "Adwati",
      description: "Explore our advanced repair tools and diagnostic solutions",
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      color: "#FFE302",
      link: "/adwati",
      bgImage: "linear-gradient(45deg, #222 30%, #333 90%)"
    },
    {
      title: "Swalhi",
      description: "Shop quality parts and accessories for your devices",
      icon: <ShoppingCartIcon sx={{ fontSize: 40 }} />,
      color: "#FFE302",
      link: "/swalhi",
      bgImage: "linear-gradient(45deg, #222 30%, #333 90%)"
    },
    {
      title: "Consultation",
      description: "Connect with our experts for personalized technical support",
      icon: <VideoCallIcon sx={{ fontSize: 40 }} />,
      color: "#FFE302",
      link: "/consultation",
      bgImage: "linear-gradient(45deg, #222 30%, #333 90%)"
    }
  ];

  return (
    <Box sx={{ 
      py: 8, 
      position: 'relative',
      bgcolor: '#2A2A2A', // Grey background different from footer
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '5px',
        background: 'linear-gradient(90deg, #FFE302, transparent 80%)'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '30%',
        height: '5px',
        background: 'linear-gradient(90deg, transparent, #FFE302)'
      }
    }}>
      {/* Decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,227,2,0.1) 0%, transparent 70%)',
      }} />
      
      <Box sx={{
        position: 'absolute',
        bottom: '15%',
        right: '8%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,227,2,0.08) 0%, transparent 70%)',
      }} />
      
      <Box sx={{
        position: 'absolute',
        top: '60%',
        left: '40%',
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,227,2,0.05) 0%, transparent 70%)',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          align="center" 
          sx={{ 
            mb: 6, 
            color: 'white',
            fontWeight: 600,
            position: 'relative',
            display: 'inline-block',
            left: '50%',
            transform: 'translateX(-50%)',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              left: '25%',
              width: '50%',
              height: '3px',
              background: 'linear-gradient(90deg, transparent, #FFE302, transparent)'
            }
          }}
        >
          Explore Our Services
        </Typography>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ 
                height: '100%',
                bgcolor: '#333',
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                  '& .card-icon': {
                    transform: 'scale(1.1) rotate(5deg)'
                  },
                  '& .card-overlay': {
                    opacity: 0.7
                  }
                }
              }}>
                <CardActionArea component={RouterLink} to={service.link} sx={{ height: '100%' }}>
                  <Box sx={{ 
                    height: 180, 
                    background: service.bgImage,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Diagonal stripes overlay */}
                    <Box 
                      className="card-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.4,
                        transition: 'opacity 0.3s ease',
                        background: `repeating-linear-gradient(
                          45deg,
                          transparent,
                          transparent 10px,
                          rgba(255,227,2,0.05) 10px,
                          rgba(255,227,2,0.05) 20px
                        )`
                      }}
                    />
                    
                    <Box 
                      className="card-icon"
                      sx={{ 
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: service.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#000',
                        transition: 'all 0.3s ease',
                        zIndex: 2,
                        boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                      }}
                    >
                      {service.icon}
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ 
                        fontWeight: 600,
                        color: service.color
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="#CCC" sx={{ mb: 2 }}>
                      {service.description}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: service.color,
                      transition: 'all 0.3s ease'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Explore Now
                      </Typography>
                      <ArrowForwardIcon sx={{ 
                        fontSize: 16, 
                        ml: 0.5,
                        transition: 'transform 0.3s ease',
                        '.MuiCardActionArea-root:hover &': {
                          transform: 'translateX(5px)'
                        }
                      }} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState('en'); // Default language
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });
  const [activeCategory, setActiveCategory] = useState('Marvel');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCookieAccept = () => {
    setShowCookieBanner(false);
  };

  const handleCookiePreferences = (preferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
  };

  const handleMenuClick = (event, menu) => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menu);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleJoin = () => {
    navigate('/join');
  };
  
  const handleLanguageChange = useCallback((lang) => {
    setCurrentLanguage(lang);
    setLanguageMenuAnchor(null);
    if (lang === 'fr') {
      navigate('/fr');
    } else if (lang === 'en') {
      navigate('/');
    }
  }, [navigate]);

  const handleContactClick = (e) => {
    e.preventDefault();
    navigate('/contact');
  };

  const handlePricingClick = () => {
    navigate('/pricing');
  };

  const menuItems = {
    'Articles': [
      'Latest Articles',
      'Featured Articles',
      'Most Popular',
      'Archive'
    ],
    'Pricing': [
      'Standard Plans',
      'Enterprise Solutions',
      'Compare Plans',
      'Custom Pricing'
    ],
    'Collections': [
      'Featured Collections',
      'User Collections',
      'Create Collection',
      'Browse All'
    ],
    'Custom Solutions': [
      'For Business',
      'For Education',
      'For Individuals',
      'Contact Sales'
    ]
  };

  const bookContent = {
    'Marvel': {
      title: "MARVEL ENCYCLOPEDIA",
      description: "Discover the essential facts about Marvel Comics timeless heroes such as Captain America, Spider-Man, and Iron Man, and villains like Thanos, Loki, and Kingpin.",
      image: "https://images.booksense.com/images/books/500/883/FC9781119883500.JPG"
    },
    'Technology': {
      title: "EMDR FOR DUMMIES",
      description: "EMDR is now recognized as one of the most effective treatment modalities for trauma, PTSD, anxiety, depression, addiction, and beyond.",
      image: "https://www.dummies.com/wp-content/uploads/astrophysics-for-dummies-cover-9781394235049.jpg"
    },
    'Academics & The Arts': {
      title: "THE ART BOOK",
      description: "Explore the rich history of visual arts through this comprehensive guide featuring renowned artists and their masterpieces.",
      image: "https://www.dummies.com/wp-content/uploads/astrophysics-for-dummies-cover-9781394235049.jpg"
    },
    'Home, Auto, & Hobbies': {
      title: "DIY HOME REPAIR",
      description: "A complete guide to home maintenance and repair, with step-by-step instructions for common household fixes.",
      image: "https://www.dummies.com/covers/9781119883715.jpg"
    },
    'Body, Mind, & Spirit': {
      title: "MINDFULNESS FOR BEGINNERS",
      description: "Learn the fundamentals of mindfulness meditation and discover how to bring peace and clarity to your daily life.",
      image: "https://www.wiley.com/remcover.cgi?isbn=9781394237302&size=300"
    },
    'Business, Careers, & Money': {
      title: "FINANCIAL INTELLIGENCE",
      description: "Master the basics of business finance and learn how to make better financial decisions in your professional life.",
      image: "https://www.wiley.com/remcover.cgi?isbn=9781394237340&size=300"
    }
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
  };

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
    handleSearchClose();
  };

  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

  const goToSolutions = () => {
    navigate('/solutions');
  };

  return (
    <Box>
      <Helmet>
        <title>Warrili - AI-Powered Technical Support & Smart Repairs</title>
        <meta name="description" content="Warrili - AI-powered technical support platform for fast solutions to technical issues" />
        <meta name="keywords" content="technical support, AI solutions, tech repair, smart repairs, device diagnostics, remote support" />
        <meta name="author" content="Warrili" />
        <meta property="og:title" content="Warrili - Smart Technical Support" />
        <meta property="og:description" content="AI-driven diagnostics and repair solutions for your technical issues" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://warrili.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@warrili" />
        <link rel="canonical" href="https://warrili.com" />
      </Helmet>
      <CircleNavLinks 
        className={isScrolled ? 'hidden' : ''}
        sx={{ 
          display: { xs: 'none', md: 'flex' }
        }}
      >
        <LinkWithIcon href="/privacy">
          <PrivacyTipIcon sx={{ fontSize: 16 }} />
          Privacy
        </LinkWithIcon>
        <LinkWithIcon href="/guidelines">
          <GavelIcon sx={{ fontSize: 16 }} />
          Guidelines
        </LinkWithIcon>
      </CircleNavLinks>

      <Box sx={{ 
        paddingTop: HEADER_HEIGHT, // Add padding top to account for fixed header
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
      }}>
        <StyledHeader>
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              height: HEADER_HEIGHT,
              py: 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LogoWrapper>
                  <img 
                    src={LogoImage} 
                    alt="Warrili" 
                    style={{ height: '28px' }}
                  />
                </LogoWrapper>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
                  <NavLink 
                    component={RouterLink} 
                    to="/solutions"  // Routes to /solutions page
                    onClick={goToSolutions}
                  >
                    Solutions
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/pricing"    // Routes to /pricing page
                    onClick={() => navigate('/pricing')}
                  >
                    Pricing
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/ai"         // Routes to /ai page
                    onClick={() => navigate('/ai')}
                  >
                    AI
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/questions"  // Routes to /questions page
                    onClick={() => navigate('/questions')}
                  >
                    Questions
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/aichat" 
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      '&:hover': { color: '#00a651' }
                    }}
                  >
                    AIChat
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/options" 
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      '&:hover': { color: '#00a651' }
                    }}
                  >
                    Options
                  </NavLink>
                  <NavLink 
                    component={RouterLink} 
                    to="/expertconsultation" 
                    sx={{ 
                      color: '#fff',
                      textDecoration: 'none',
                      '&:hover': { color: '#00a651' }
                    }}
                  >
                    Consultation
                  </NavLink>
                </Box>
                
                {/* LinkedIn Icon - Blue color as requested - Only visible on desktop */}
                <IconButton
                  component="a"
                  href="https://www.linkedin.com/company/warrili/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{ 
                    display: { xs: 'none', md: 'flex' },
                    color: '#0077B5',  /* LinkedIn blue color */
                    '&:hover': { color: '#0077B5', opacity: 0.8 }
                  }}
                >
                  <LinkedIn />
                </IconButton>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2
              }}>
                {/* Language Switcher - Only visible on desktop */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, position: 'relative', mr: 2 }}>
                  <IconButton
                    aria-label="Switch Language"
                    onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
                    sx={{ 
                      display: { xs: 'none', md: 'flex' },
                      padding: '4px'
                    }}
                  >
                    <img 
                      src={SwitchIcon} 
                      alt="Switch Language" 
                      style={{ width: '24px', height: '24px' }} 
                    />
                  </IconButton>
                  <Menu
                    anchorEl={languageMenuAnchor}
                    open={Boolean(languageMenuAnchor)}
                    onClose={() => setLanguageMenuAnchor(null)}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        border: '1px solid #333333',
                        '& .MuiMenuItem-root:hover': {
                          backgroundColor: '#333333'
                        }
                      }
                    }}
                  >
                    <MenuItem onClick={() => handleLanguageChange('en')}>
                      <Typography variant="body2">English</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleLanguageChange('fr')}>
                      <Typography variant="body2">Français</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleLanguageChange('ar')}>
                      <Typography variant="body2">العربية</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
                
                {/* Login and Join buttons - Only visible on desktop */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, mr: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLogin}
                    sx={{
                      color: '#ffffff',
                      borderColor: '#ffffff',
                      borderRadius: 0,
                      textTransform: 'none',
                      padding: '6px 16px',
                      minWidth: '80px',
                      '&:hover': {
                        borderColor: '#ffffff',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleJoin}
                    sx={{
                      backgroundColor: '#FFE302',
                      color: '#000000',
                      borderRadius: 0,
                      textTransform: 'none',
                      padding: '6px 16px',
                      minWidth: '80px',
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: '#e6cc00'
                      }
                    }}
                  >
                    Join
                  </Button>
                </Box>
              
                <TextField 
                  size="small"
                  placeholder="Suchen"
                  sx={{ 
                    display: { xs: 'none', md: 'block' },
                    '& .MuiInputBase-root': {
                      height: '32px',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: '#ffffff'
                      },
                      '&:hover fieldset': {
                        borderColor: '#ffffff'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ffffff'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff'
                    },
                    '& input::placeholder': { color: 'rgba(255,255,255,0.7)' }
                  }}
                />
                
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' }, 
                  alignItems: 'center',
                  gap: 1,
                  color: '#fff'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    ml: 1
                  }}>
                    <Typography sx={{ 
                      fontSize: '14px',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      AI-Powered Diagnostics
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '12px',
                      fontFamily: 'Arial, sans-serif'
                    }}>
                      Fast & Accurate Solutions
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <IconButton 
                sx={{ 
                  display: { xs: 'block', md: 'none' },
                  color: '#ffffff'
                }}
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
              <IconButton 
                sx={{ 
                  display: { xs: 'block', md: 'none' },
                  color: '#ffffff'
                }}
                onClick={handleSearchClick}
              >
                <img 
                  src={SearchIconImage} 
                  alt="Search" 
                  style={{ 
                    width: '24px', 
                    height: '24px',
                    filter: 'brightness(0) invert(1)' // Makes the icon white
                  }} 
                />
              </IconButton>
            </Box>
          </Container>

          <SearchBar>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                alignItems: 'center',
                position: 'relative',
                zIndex: 1
              }}>
                <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: '0', overflow: 'hidden', width: { xs: '100%', md: '500px' } }}>
                  <SearchInputWrapper>
                    <SearchIconImg 
                      src={Search2Icon}
                      alt="Search"
                    />
                    <SearchInput 
                      placeholder="Search for device issues or repair solutions"
                    />
                  </SearchInputWrapper>
                </Box>
                <ScanButton 
                  onClick={() => navigate('/scan')}
                  sx={{
                    borderRadius: '0',
                    fontWeight: 'bold',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    '&:hover': {
                      backgroundColor: '#444'
                    }
                  }}
                >
                  <img 
                    src={FingerprintIcon} 
                    alt="Fingerprint" 
                    style={{ 
                      width: '18px', 
                      height: '18px',
                      filter: 'brightness(0) invert(1)' // Makes the icon white
                    }} 
                  />
                  Scan
                </ScanButton>
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' }, 
                  alignItems: 'center',
                  gap: 1,
                  color: '#fff'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    ml: 1
                  }}>
                    <Typography sx={{ 
                      fontSize: '14px',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      AI-Powered Diagnostics
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '12px',
                      fontFamily: 'Arial, sans-serif'
                    }}>
                      Fast & Accurate Solutions
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Container>
          </SearchBar>
        </StyledHeader>

        <Box sx={{ 
          mt: 0, 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <RepairSection />
          <HowItWorksSection />
          
          <Box sx={{ 
            bgcolor: '#FFE302', 
            py: 8,
            mt: 4
          }}>
            <Container maxWidth="lg">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box
                    component="img"
                    src={LogoImage}
                    alt="Larravaris Logo"
                    sx={{
                      width: '200px',
                      height: 'auto',
                      mb: 4
                    }}
                  />
                  
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: '#000'
                    }}
                  >
                    About Larravaris.com
                  </Typography>
                  
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 3,
                      color: '#000'
                    }}
                  >
                    Your Trusted AI-Powered Repair Solution
                  </Typography>

                  <Box sx={{ 
                    borderLeft: '4px solid #000', 
                    pl: 2,
                    mb: 4
                  }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#000',
                        lineHeight: 1.5
                      }}
                    >
                      Our editorial team works with you to create marketing materials that your clients just <i>get</i>.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    href="/contact"
                    sx={{
                      bgcolor: '#000',
                      color: '#fff',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        bgcolor: '#333'
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                </Grid>

                <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={require('../assets/repair.png')}
                      alt="Custom Solutions for Dummies"
                      style={{
                        maxWidth: '80%',
                        height: 'auto',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: -30,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        textAlign: 'center',
                        color: '#000',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#333'
                        }
                      }}
                    >
                      Download our free eBook<br />
                      or listen to the audiobook
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>

          <Box sx={{ py: 8, bgcolor: '#fff' }}>
            <Container maxWidth="lg">
              <Typography
                variant="h3"
                align="center"
                sx={{
                  mb: 6,
                  color: '#0077B5',
                  fontWeight: 500
                }}
              >
                Engage and convert your customers at every touchpoint
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                {serviceItems.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          '& .image-container': {
                            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                          },
                          '& .read-more-link': {
                            color: '#000',
                            '&::after': {
                              width: '100%',
                              backgroundColor: '#FFE302',
                            }
                          }
                        }
                      }}
                    >
                      <Box
                        className="image-container"
                        sx={{
                          width: '100%',
                          height: '180px',
                          mb: 3,
                          overflow: 'hidden',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          transition: 'box-shadow 0.3s ease-in-out'
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.alt}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: '#333',
                          minHeight: '64px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#666',
                          fontSize: '14px',
                          lineHeight: 1.6,
                          flex: 1,
                          mb: 3
                        }}
                      >
                        {item.description}
                      </Typography>
                      <Link
                        className="read-more-link"
                        component={RouterLink}
                        to={item.link}
                        sx={{
                          mt: 'auto',
                          color: '#333',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 500,
                          position: 'relative',
                          transition: 'color 0.3s ease-in-out',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-2px',
                            left: 0,
                            width: '0',
                            height: '2px',
                            backgroundColor: '#FFE302',
                            transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                          },
                          '&:hover': {
                            color: '#000',
                            '&::after': {
                              width: '100%',
                              backgroundColor: '#FFE302'
                            }
                          }
                        }}
                      >
                        Read More
                      </Link>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          <Box sx={{ flex: 1, mt: 8 }}>
            <Container maxWidth="lg">
            </Container>
          </Box>
        </Box>

        <ShowcaseSection />

        <Footer>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Typography variant="h6" gutterBottom>
                  Quick Links
                </Typography>
                <Stack spacing={1}>
                  <FooterLink variant="body2">
                    About Innospherri
                  </FooterLink>
                  <FooterLink 
                    variant="body2" 
                    component="button"
                    onClick={handlePricingClick}
                    sx={{ 
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      padding: 0,
                      width: '100%',
                      color: '#fff',
                      '&:hover': {
                        color: '#ccc'
                      }
                    }}
                  >
                    Pricing Plans
                  </FooterLink>
                  <FooterLink 
                    variant="body2"
                    onClick={handleContactClick}
                    sx={{ cursor: 'pointer' }}
                  >
                    Contact Us
                  </FooterLink>
                  <FooterLink variant="body2">
                    Smart Search
                  </FooterLink>
                  <FooterLink variant="body2">
                    Activate Premium Access
                  </FooterLink>
                </Stack>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="h6" gutterBottom>
                  Connect
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                  <SocialIconButton>
                    <Facebook />
                  </SocialIconButton>
                  <SocialIconButton>
                    <Twitter />
                  </SocialIconButton>
                  <SocialIconButton>
                    <LinkedIn />
                  </SocialIconButton>
                </Stack>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Newsletter
                </Typography>
                <Box component="form" sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter your email"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '& .MuiInputBase-root': {
                        height: '32px',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                      },
                      '& input::placeholder': { color: 'rgba(255,255,255,0.7)' }
                    }}
                  />
                  <Button 
                    variant="contained"
                    sx={{
                      bgcolor: '#007C3F',
                      '&:hover': { bgcolor: '#006633' }
                    }}
                  >
                    Subscribe
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src={LogoImage}
                  alt="Innospherri Logo"
                  sx={{
                    width: '200px',
                    height: 'auto',
                    mb: 3,
                    filter: 'brightness(0) invert(1)', // Makes the logo white
                  }}
                />
                <Typography variant="h6" gutterBottom>
                  About Innospherri
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Innospherri has always stood for innovation and knowledge sharing. We help everyone become more knowledgeable and confident in applying what they learn. Whether it's to master new technologies, advance in their careers, or develop creative skills, people who rely on Innospherri trust it to learn the critical skills and relevant information necessary for success.
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ 
              borderTop: '1px solid rgba(255,255,255,0.1)',
              mt: 4,
              pt: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="body2" color="rgba(255,255,255,0.7)">
                2024 Innospherri Inc. All rights reserved.
              </Typography>
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2}
                sx={{ 
                  '& > *': { 
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      color: '#fff',
                      cursor: 'pointer'
                    }
                  }
                }}
              >
                <Typography variant="body2">
                  Terms of Use
                </Typography>
                <Typography variant="body2">
                  Privacy Policy
                </Typography>
                <Typography variant="body2">
                  Cookie Settings
                </Typography>
                <Typography variant="body2">
                  Do Not Sell My Personal Info
                </Typography>
              </Stack>
            </Box>
          </Container>
        </Footer>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '280px',
              backgroundColor: '#000000',
              color: '#ffffff'
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <img 
                  src={LogoImage} 
                  alt="Warrili" 
                  style={{ height: '30px' }}
                />
              </Box>
              <IconButton 
                onClick={() => setMobileMenuOpen(false)}
                sx={{ color: '#ffffff' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
            
            <List>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/solutions"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  Solutions
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/pricing"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  Pricing
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/ai"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  AI
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/questions"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  Questions
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/aichat"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  AIChat
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/options"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  Options
                </Button>
              </ListItem>
              <ListItem>
                <Button 
                  component={RouterLink} 
                  to="/expertconsultation"
                  fullWidth
                  sx={{ 
                    justifyContent: 'flex-start',
                    color: '#ffffff',
                    textTransform: 'none',
                    '&:hover': { color: '#FFE302' }
                  }}
                >
                  Consultation
                </Button>
              </ListItem>
            </List>
            
            <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
            
            {/* Social Media Icons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <IconButton 
                component="a"
                href="https://www.linkedin.com/company/warrili/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={{ color: '#ffffff' }}
              >
                <LinkedIn />
              </IconButton>
              <IconButton 
                component="a"
                href="https://twitter.com/warrili"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                sx={{ color: '#ffffff' }}
              >
                <Twitter />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button 
                variant="outlined"
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: '#FFE302',
                    color: '#FFE302'
                  }
                }}
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
              <Button 
                variant="contained"
                sx={{ 
                  bgcolor: '#007C3F',
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: '#006633'
                  }
                }}
                onClick={handleJoin}
              >
                Join now
              </Button>
            </Box>
          </Box>
        </Drawer>

        <CookieBanner 
          open={showCookieBanner}
          onClose={() => setShowCookieBanner(false)}
          onAccept={handleCookiePreferences}
        />

        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              maxWidth: '300px'
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <LogoWrapper>
                <img src={LogoImage} alt="Logo" />
              </LogoWrapper>
              <IconButton onClick={() => setMobileMenuOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <NavMenu />

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <AuthButton 
                variant="outlined"
                fullWidth
                onClick={handleLogin}
                sx={{ 
                  borderColor: '#000',
                  color: '#000'
                }}
              >
                Log in
              </AuthButton>
              <AuthButton 
                variant="contained"
                fullWidth
                onClick={handleJoin}
                sx={{
                  backgroundColor: '#007C3F',
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: '#006633'
                  },
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  borderRadius: 1
                }}
              >
                Join now
              </AuthButton>
              <Link
                href="/privacy"  // Privacy first now
                sx={{
                  color: '#000',
                  textDecoration: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    color: '#007C3F'
                  }
                }}
              >
                Privacy
              </Link>
              <Link
                href="/guidelines"  // Guidelines second now
                sx={{
                  color: '#000',
                  textDecoration: 'none',
                  fontSize: '14px',
                  '&:hover': {
                    color: '#007C3F'
                  }
                }}
              >
                Guidelines
              </Link>
            </Box>
          </Box>
        </Drawer>

        <ChatButton onClick={toggleChat}>
          <ChatIcon />
        </ChatButton>

        {chatOpen && (
          <ChatBox>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid #eee',
              backgroundColor: '#000',
              color: '#fff'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Chat with us
              </Typography>
            </Box>
            
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography variant="body2" sx={{ 
                backgroundColor: '#f5f5f5',
                p: 1.5,
                borderRadius: '8px',
                alignSelf: 'flex-start',
                maxWidth: '80%'
              }}>
                Hello! How can I help you today?
              </Typography>
            </Box>

            <Box sx={{ 
              p: 2, 
              borderTop: '1px solid #eee',
              display: 'flex',
              gap: 1
            }}>
              <InputBase
                placeholder="Type a message..."
                sx={{ flex: 1 }}
              />
              <IconButton sx={{ color: '#000' }}>
                <SendIcon />
              </IconButton>
            </Box>
          </ChatBox>
        )}
      </Box>
      <Chatbot isHomePage={true} />

      {/* Mobile Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={handleSearchClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 0,
            backgroundColor: 'rgba(0,0,0,0.95)',
            color: '#ffffff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            position: 'absolute',
            top: 0,
            m: 0,
            width: '100%',
            maxHeight: '100%',
            height: 'auto',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            pt: 2
          }}>
            {/* Search Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              px: 2,
              mb: 2
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  letterSpacing: '0.5px'
                }}
              >
                Search Warrili
              </Typography>
              <IconButton 
                onClick={handleSearchClose} 
                sx={{ 
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Search Form */}
            <Box sx={{ px: 2, mb: 3 }}>
              <form onSubmit={handleSearchSubmit}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: '#ffffff', 
                    borderRadius: 0,
                    border: '2px solid #FFE302',
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ pl: 2 }}>
                    <SearchIcon sx={{ color: '#000000' }} />
                  </Box>
                  <InputBase
                    fullWidth
                    placeholder="What are you looking for?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ 
                      color: '#000000', 
                      py: 1.5, 
                      px: 2,
                      fontSize: '16px',
                      fontFamily: 'Arial, sans-serif',
                      '&::placeholder': {
                        fontStyle: 'italic',
                        color: 'rgba(0,0,0,0.6)'
                      }
                    }}
                    autoFocus
                  />
                  <IconButton 
                    type="submit" 
                    sx={{ 
                      color: '#000000', 
                      bgcolor: '#FFE302',
                      borderRadius: 0,
                      height: '100%',
                      px: 2,
                      '&:hover': {
                        bgcolor: '#e6cc00'
                      }
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </form>
            </Box>
            
            {/* Quick Categories */}
            <Box sx={{ px: 2, mb: 4 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#FFE302', 
                  mb: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '12px'
                }}
              >
                Quick Categories
              </Typography>
              <Grid container spacing={1}>
                {['Hardware', 'Software', 'Network', 'Mobile'].map((category) => (
                  <Grid item xs={6} key={category}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setSearchQuery(category);
                        handleSearchSubmit({ preventDefault: () => {} });
                      }}
                      sx={{
                        color: '#ffffff',
                        borderColor: 'rgba(255,255,255,0.3)',
                        borderRadius: 0,
                        justifyContent: 'flex-start',
                        py: 1,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#FFE302',
                          backgroundColor: 'rgba(255,227,2,0.1)'
                        }
                      }}
                    >
                      {category}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* Popular Searches */}
            <Box sx={{ px: 2, flex: 1, overflow: 'auto' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: '#FFE302', 
                  mb: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '12px'
                }}
              >
                Popular Searches
              </Typography>
              <List sx={{ p: 0 }}>
                {[
                  'iPhone screen repair', 
                  'Laptop not turning on', 
                  'WiFi connection issues', 
                  'Battery replacement',
                  'Windows blue screen',
                  'Mac startup problems',
                  'Android phone slow'
                ].map((item, index) => (
                  <ListItem 
                    key={index} 
                    button 
                    onClick={() => {
                      setSearchQuery(item);
                      handleSearchSubmit({ preventDefault: () => {} });
                    }}
                    sx={{ 
                      py: 1.5, 
                      px: 0,
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', mr: 2, fontSize: 20 }} />
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{
                        sx: { 
                          fontFamily: 'Arial, sans-serif',
                          fontSize: '15px'
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Home;
